import React, { useState } from 'react';
import * as XLSX from 'xlsx';

const ExcelValidator = () => {
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState(null);

  const handleFileUpload = async (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    setFile(uploadedFile);
    setError(null);
    setIsAnalyzing(true);

    try {
      // Read Excel file
      const fileBuffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(fileBuffer);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Send to backend for analysis
      const response = await fetch('/api/validate-excel-file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileData: jsonData,
          fileName: uploadedFile.name
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setAnalysis(result.analysis);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(`Error processing file: ${err.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg border">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">
            📊 Excel File Validator
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Sube tu archivo Excel para validar la estructura y datos
          </p>
        </div>

        <div className="p-6">
          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Archivo Excel
            </label>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
          </div>

          {/* Loading */}
          {isAnalyzing && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-sm text-gray-600 mt-2">Analizando archivo...</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-600">❌ {error}</p>
            </div>
          )}

          {/* Analysis Results */}
          {analysis && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="font-medium text-green-900 mb-2">
                  ✅ Archivo Analizado Exitosamente
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-green-700">Total Filas:</span>
                    <p className="font-medium">{analysis.totalRows}</p>
                  </div>
                  <div>
                    <span className="text-green-700">Total Columnas:</span>
                    <p className="font-medium">{analysis.columns.length}</p>
                  </div>
                  <div>
                    <span className="text-green-700">Campos Precio:</span>
                    <p className="font-medium">{analysis.priceFields.length}</p>
                  </div>
                  <div>
                    <span className="text-green-700">Campos Stock:</span>
                    <p className="font-medium">{analysis.stockFields.length}</p>
                  </div>
                </div>
              </div>

              {/* Field Mapping */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">📝 Títulos</h4>
                  <ul className="text-sm space-y-1">
                    {analysis.titleFields.map(field => (
                      <li key={field} className="text-blue-700">• {field}</li>
                    ))}
                  </ul>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-2">💰 Precios</h4>
                  <ul className="text-sm space-y-1">
                    {analysis.priceFields.map(field => (
                      <li key={field} className="text-yellow-700">• {field}</li>
                    ))}
                  </ul>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h4 className="font-medium text-purple-900 mb-2">📦 Stock</h4>
                  <ul className="text-sm space-y-1">
                    {analysis.stockFields.map(field => (
                      <li key={field} className="text-purple-700">• {field}</li>
                    ))}
                  </ul>
                  <p className="text-xs text-purple-600 mt-2">
                    ⚠️ Stock será forzado a 0
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="font-medium text-green-900 mb-2">🏷️ Categorías</h4>
                  <ul className="text-sm space-y-1">
                    {analysis.categoryFields.map(field => (
                      <li key={field} className="text-green-700">• {field}</li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Sample Data */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">📋 Muestra de Datos (primeras 3 filas)</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        {analysis.columns.map(col => (
                          <th key={col} className="px-3 py-2 text-left font-medium text-gray-700 border-r">
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {analysis.sampleData.map((row, index) => (
                        <tr key={index} className="border-t">
                          {analysis.columns.map(col => (
                            <td key={col} className="px-3 py-2 border-r text-gray-600">
                              {String(row[col] || '').substring(0, 50)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* All Columns */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">📋 Todas las Columnas Detectadas</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                    {analysis.columns.map(col => (
                      <div key={col} className="bg-white rounded px-2 py-1 text-gray-700">
                        {col}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcelValidator;